"use client";

import React, { useTransition, MouseEvent, useCallback } from "react";
import { useStatsigClient } from "@statsig/react-bindings";
import { useRoutePathname, type StatsigEvent } from "@repo/statsig";
import { ActionButtonProps } from "./types";
import { defaultTheme } from "./themes";
import { mergeTheme } from "../utils/theme";

/**
 * ActionButton Component
 *
 * A comprehensive button component with support for loading states, transitions,
 * analytics tracking, and accessibility features. Uses React 18 transitions
 * for smooth UI updates and supports both onClick and legacy onPress handlers.
 *
 * @param children - Button content (alternative to text prop)
 * @param text - Button text content
 * @param onClick - Click handler function
 * @param onPress - Legacy click handler for backward compatibility
 * @param disabled - Whether the button is disabled
 * @param loading - Whether the button shows loading state
 * @param type - HTML button type (button, submit, reset)
 * @param form - Form ID for submit buttons
 * @param theme - Theme overrides for styling
 * @param className - Additional CSS classes
 * @param style - Inline styles
 * @param pill - Whether to apply pill styling
 * @param eventData - Analytics event data
 * @param customEventName - Custom analytics event name
 * @param ariaLabel - Accessibility label
 * @param ariaPressed - ARIA pressed state
 * @param ariaExpanded - ARIA expanded state
 * @param data-testid - Test identifier
 */
export const ActionButton: React.FC<ActionButtonProps> = ({
  children,
  text,
  onClick,
  onPress, // Legacy support
  disabled = false,
  loading = false,
  type = "button",
  form,
  theme,
  className = "",
  style,
  pill = false,
  eventData,
  customEventName,
  ariaLabel,
  ariaPressed,
  ariaExpanded,
  "data-testid": dataTestId,
}) => {
  const [isPending, startTransition] = useTransition();
  const mergedTheme = mergeTheme(defaultTheme, theme);
  const { logEvent } = useStatsigClient();
  const locationData = useRoutePathname();

  // Determine if button is in loading state
  const isLoading = loading || isPending;
  const isDisabled = disabled || isLoading;

  // Build className from theme
  const themeClasses = [
    mergedTheme.base,
    mergedTheme.hover,
    mergedTheme.active,
    mergedTheme.focus,
    isDisabled && mergedTheme.disabled,
    isLoading && mergedTheme.loading,
    pill && mergedTheme.pill,
  ]
    .filter(Boolean)
    .join(" ");

  // Combine theme classes with custom className
  const finalClassName = `${themeClasses} ${className}`.trim();

  // Handle click with transition for non-form buttons
  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      // Blur the button to remove focus
      (event.target as HTMLButtonElement).blur();

      // Log analytics events with Statsig
      if (eventData) {
        // Log the standard button_pressed event
        const standardEvent: StatsigEvent = {
          eventName: "button_pressed",
          metadata: { ...locationData, ...eventData },
        };
        logEvent(standardEvent);

        // Log the custom event if provided
        if (customEventName) {
          const customEvent: StatsigEvent = {
            eventName: customEventName,
            metadata: { ...locationData, ...eventData },
          };
          logEvent(customEvent);
        }
      }

      // Call the appropriate handler
      if (onClick) {
        onClick(event);
      } else if (onPress) {
        onPress();
      }
    },
    [onClick, onPress, eventData, customEventName, logEvent, locationData]
  );

  // Wrap click handler in transition for non-form buttons
  const handleTransitionClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      // For submit buttons, let the browser handle form submission naturally
      if (type === "submit") {
        handleClick(event);
        // Don't prevent default - let form submission happen
        return;
      }

      if (!form) {
        startTransition(() => {
          handleClick(event);
        });
      } else {
        handleClick(event);
      }
    },
    [form, type, handleClick]
  );

  return (
    <button
      type={type}
      disabled={isDisabled}
      onClick={handleTransitionClick}
      className={finalClassName}
      style={style}
      form={form}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      aria-expanded={ariaExpanded}
      data-testid={dataTestId}
    >
      {text || children}
    </button>
  );
};

ActionButton.displayName = "ActionButton";
